// src/components/Gallery.js
// Author: Site Crafted

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import "../styles/Gallery.css";

const GALLERY_IMAGES = [
  { id: 1, src: "/images/lash1.jpg", alt: "Lash With Brittany - Classic Lashes", tag: "Classic" },
  { id: 2, src: "/images/lash2.jpg", alt: "Lash With Brittany - Volume Lashes", tag: "Volume" },
  { id: 3, src: "/images/lash3.jpg", alt: "Lash With Brittany - Classic Lashes", tag: "Classic" },
  { id: 4, src: "/images/lash4.jpg", alt: "Lash With Brittany - Volume Lashes", tag: "Volume" },
  { id: 5, src: "/images/lash5.jpg", alt: "Lash With Brittany - Volume Lashes", tag: "Volume" },
  { id: 6, src: "/images/lash6.jpg", alt: "Lash With Brittany - Hybrid Lashes", tag: "Hybrid" },
  { id: 7, src: "/images/lash7.jpg", alt: "Lash With Brittany - Classic Lashes", tag: "Classic" },
  { id: 8, src: "/images/lash8.jpg", alt: "Lash With Brittany - Volume Lashes", tag: "Volume" },
  { id: 9, src: "/images/lash9.jpg", alt: "Lash With Brittany - Lash Lift and Brow Wax", tag: "Lift and Brow Wax" },
  { id: 10, src: "/images/lash10.jpg", alt: "Lash With Brittany - Volume Lashes", tag: "Volume" },
  { id: 11, src: "/images/lash11.jpg", alt: "Lash With Brittany - Classic Lashes", tag: "Classic" },
  { id: 12, src: "/images/lash12.jpg", alt: "Lash With Brittany - Hybrid Lashes", tag: "Hybrid" },
  { id: 13, src: "/images/lash13.jpg", alt: "Lash With Brittany - Volume Lashes", tag: "Volume" },
  { id: 14, src: "/images/lash14.jpg", alt: "Lash With Brittany - Mega Volume Lashes", tag: "Mega Volume" },
  { id: 15, src: "/images/lash15.jpg", alt: "Lash With Brittany - Hybrid Lashes", tag: "Hybrid" },
  { id: 16, src: "/images/lash16.jpg", alt: "Lash With Brittany - Mega Volume Lashes", tag: "Mega Volume" },
  { id: 17, src: "/images/lash17.jpg", alt: "Lash With Brittany - Wet Lash Look", tag: "Wet Lash Look" },
  { id: 18, src: "/images/lash18.jpg", alt: "Lash With Brittany - Hybrid Lashes", tag: "Hybrid" },
];

function Gallery() {
  const [activeTag, setActiveTag] = useState("All");
  const [selectedIndex, setSelectedIndex] = useState(null);

  const lastFocusedElement = useRef(null);
  const closeBtnRef = useRef(null);

  const tags = useMemo(() => {
    const unique = Array.from(new Set(GALLERY_IMAGES.map((img) => img.tag)));
    return ["All", ...unique];
  }, []);

  const visibleImages = useMemo(() => {
    if (activeTag === "All") return GALLERY_IMAGES;
    return GALLERY_IMAGES.filter((img) => img.tag === activeTag);
  }, [activeTag]);

  const selectedImage = selectedIndex === null ? null : visibleImages[selectedIndex];

  const openImage = (index) => {
    lastFocusedElement.current = document.activeElement;
    setSelectedIndex(index);

    if (typeof window.gtag === "function") {
      const img = visibleImages[index];
      window.gtag("event", "open_image", {
        event_category: "Gallery",
        event_label: img?.alt || "image",
      });
    }
  };

  const closeImage = () => {
    setSelectedIndex(null);
  };

  const goPrev = useCallback(() => {
    setSelectedIndex((prev) => {
      if (prev === null) return prev;
      return (prev - 1 + visibleImages.length) % visibleImages.length;
    });
  }, [visibleImages.length]);

  const goNext = useCallback(() => {
    setSelectedIndex((prev) => {
      if (prev === null) return prev;
      return (prev + 1) % visibleImages.length;
    });
  }, [visibleImages.length]);

  useEffect(() => {
    if (!selectedImage) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeImage();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedImage, goPrev, goNext]);

  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = "hidden";
      window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    } else {
      document.body.style.overflow = "unset";
      if (lastFocusedElement.current) lastFocusedElement.current.focus();
    }
  }, [selectedImage]);

  useEffect(() => {
    // When switching filters while modal is open, keep selection valid
    if (selectedIndex === null) return;
    if (selectedIndex > visibleImages.length - 1) setSelectedIndex(0);
  }, [activeTag, selectedIndex, visibleImages.length]);

  return (
    <main className="gallery-page" role="region" aria-labelledby="gallery-heading">
      <Helmet>
        <title>Gallery | Lash With Brittany</title>
        <meta
          name="description"
          content="View our gallery of professional lash extension services, including classic, hybrid, volume, and mega lashes. Lash With Brittany in Rochester, NY brings your beauty to life."
        />
        <meta property="og:title" content="Lash With Brittany | Gallery" />
        <meta
          property="og:description"
          content="Explore lash styles including classic, hybrid, volume, mega volume, wet lash look, and lash lift with brow wax."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lashwithbrittany.com/gallery" />
        <meta property="og:image" content="https://lashwithbrittany.com/hero-bg-square.png" />
        <link rel="canonical" href="https://lashwithbrittany.com/gallery" />
      </Helmet>

      <section className="gallery-hero">
        <div className="gallery-shell">
          <p className="gallery-eyebrow">Rochester, New York</p>
          <h1 id="gallery-heading" className="gallery-title">Gallery</h1>
          <p className="gallery-subtitle">
            A quick look at real sets, real results, and the styles you can book.
          </p>

          <div className="gallery-hero-actions">
            <a className="gallery-btn gallery-btn-primary" href="/book">Book Now</a>
            <a className="gallery-btn gallery-btn-secondary" href="/contact-me">Questions</a>
          </div>

          <div className="gallery-filters" role="tablist" aria-label="Gallery filters">
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                className={`gallery-chip ${activeTag === t ? "is-active" : ""}`}
                onClick={() => setActiveTag(t)}
                role="tab"
                aria-selected={activeTag === t}
              >
                {t}
              </button>
            ))}
          </div>

          <p className="gallery-count" aria-live="polite">
            Showing <strong>{visibleImages.length}</strong> photos
          </p>
        </div>
      </section>

      <section className="gallery-section" aria-label="Gallery grid">
        <div className="gallery-shell">
          <div className="gallery-grid">
            {visibleImages.map((image, index) => (
              <button
                key={image.id}
                type="button"
                className="gallery-tile"
                onClick={() => openImage(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openImage(index);
                }}
                aria-label={`Open photo: ${image.alt}`}
              >
                <span className="gallery-frame">
                  <span className="gallery-frame-white">
                    <span className="gallery-media">
                      <img src={image.src} alt={image.alt} loading="lazy" />
                      <span className="gallery-overlay" aria-hidden="true">
                        <span className="gallery-tag">{image.tag}</span>
                        <span className="gallery-hint">Tap to view</span>
                      </span>
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {selectedImage && (
        <div
          className="gallery-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          onClick={closeImage}
        >
          <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-modal-top">
              <div className="gallery-modal-meta">
                <span className="gallery-modal-tag">{selectedImage.tag}</span>
                <span className="gallery-modal-caption">{selectedImage.alt}</span>
              </div>

              <button
                ref={closeBtnRef}
                type="button"
                className="gallery-modal-close"
                onClick={closeImage}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="gallery-modal-media">
              <img src={selectedImage.src} alt={selectedImage.alt} />
            </div>

            <div className="gallery-modal-actions">
              <button type="button" className="gallery-modal-nav" onClick={goPrev} aria-label="Previous photo">
                Prev
              </button>
              <button type="button" className="gallery-modal-nav" onClick={goNext} aria-label="Next photo">
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Gallery;
